import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconChannelsOrder,
  IconDelete,
  IconDuplicate,
  IconEdit,
  IconGroups,
} from '../../../../../assets/popup-nav-icons';
import { PopUpNavLink } from '../../../../../components/popup-nav';
import {
  ActionType,
  DashboardItemType,
  UserAction,
  UserPermissionType,
} from '../../../../../data-access/gql-types/graphql';
import { useUserPermissions } from '../../../../../hooks/user/use-user-permissions';
import { ROUTES } from '../../../../../routes/routes';
import { useUserProfileStore } from '../../../../../store/user-profile';
import { useModifyMultipleGroups } from '../../channel-details/hooks/use-modify-multiple-groups';

export const useActionDetails = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('action');
  const { t: td } = useTranslation('channel-details');
  const { actionId } = useParams<{ actionId: string }>();
  const [userAction, setUserAction] = useState<UserAction | null>();
  const [showDuplicateDialog, setShowDuplicateDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const { permissions } = useUserPermissions();
  const { userId } = useUserProfileStore((s) => s.meData);
  const { groupList, handleSaveGroup, toggleGroup, groupPopup, setGroupPopup } = useModifyMultipleGroups({
    itemId: actionId || '',
    itemType: DashboardItemType.Action,
  });

  const canRemoveAction = useMemo(
    () =>
      userAction?.action.ownerId === userId
        ? permissions[UserPermissionType.OwnActionsRemove]
        : permissions[UserPermissionType.ActionsRemove],
    [(userAction?.action.ownerId, userId, permissions)],
  );

  const canEditAction = useMemo(
    () =>
      userAction?.action.ownerId === userId
        ? permissions[UserPermissionType.OwnActionsEdit]
        : permissions[UserPermissionType.ActionsEdit],
    [(userAction?.action.ownerId, userId, permissions)],
  );

  const popupLinks: PopUpNavLink[] = useMemo(() => {
    if (!userAction) return [];
    switch (userAction?.action.actionType) {
      case ActionType.Central:
        return [
          ...(canEditAction
            ? [
                {
                  onClick: () => navigate(ROUTES.ActionChannelsSort(userAction.action.id)),
                  label: t('sort'),
                  icon: <IconChannelsOrder />,
                },
              ]
            : []),
        ];
      case ActionType.Basic:
        return [
          ...(canEditAction
            ? [
                {
                  onClick: () => navigate(ROUTES.ActionEditName(userAction.action.id)),
                  label: t('changeName'),
                  icon: <IconEdit />,
                },
                {
                  onClick: () => navigate(ROUTES.ActionEditChannels(userAction.action.id)),
                  label: t('edit'),
                  icon: <IconEdit />,
                },
                {
                  onClick: () => navigate(ROUTES.ActionChannelsSort(userAction.action.id)),
                  label: t('sort'),
                  hasLineBelow: true,
                  icon: <IconChannelsOrder />,
                },
              ]
            : []),
          ...(permissions[UserPermissionType.AddRemoveChannelForMultipleGroups] ||
          permissions[UserPermissionType.AddRemoveChannelForMultiplePublicGroups]
            ? [
                {
                  onClick: () => setGroupPopup(true),
                  label: td('groupAddRemove'),
                  icon: <IconGroups />,
                },
              ]
            : []),
          ...(canRemoveAction
            ? [{ onClick: () => setShowDeleteDialog(true), label: t('remove'), icon: <IconDelete /> }]
            : []),
        ];
      case ActionType.Advanced:
        return [
          ...(canEditAction
            ? [
                {
                  onClick: () => navigate(ROUTES.ActionEdit(userAction.action.id)),
                  label: t('edit'),
                  icon: <IconEdit />,
                },
                {
                  onClick: () => navigate(ROUTES.ActionChannelsSort(userAction.action.id)),
                  label: t('sort'),
                  hasLineBelow: true,
                  icon: <IconChannelsOrder />,
                },
              ]
            : []),
          ...(permissions[UserPermissionType.AddRemoveChannelForMultipleGroups] ||
          permissions[UserPermissionType.AddRemoveChannelForMultiplePublicGroups]
            ? [
                {
                  onClick: () => setGroupPopup(true),
                  label: td('groupAddRemove'),
                  icon: <IconGroups />,
                },
              ]
            : []),
          ...(permissions[UserPermissionType.ActionsCreate]
            ? [{ onClick: () => setShowDuplicateDialog(true), label: t('duplicate'), icon: <IconDuplicate /> }]
            : []),
          ...(canRemoveAction
            ? [{ onClick: () => setShowDeleteDialog(true), label: t('remove'), icon: <IconDelete /> }]
            : []),
        ];
      default:
        return [];
    }
  }, [userAction, canEditAction, canRemoveAction, permissions]);

  return {
    popupLinks,
    groupList,
    handleSaveGroup,
    toggleGroup,
    groupPopup,
    setGroupPopup,
    userAction,
    setUserAction,
    showDeleteDialog,
    showDuplicateDialog,
    setShowDeleteDialog,
    setShowDuplicateDialog,
  };
};
